// P0 value-add features (canonical prompts 111–124, renumbered from the P0 backlog pack's
// 97–110 to append after the existing canonical 97 AI Cue Director). These are advisory /
// compute services following the established value-add idiom: pure, deterministic, stateless
// (callers persist results via existing project routes). Fixed timestamp + SeqCounter keep
// outputs deterministic for tests. Nothing here duplicates canonical stores.
import { SeqCounter } from "@djsem/shared";
import { DJSEMTransitionRiskAnalyzer } from "./transitionAssistant";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

/** Shared track shape used across the P0 features (superset of the fields each needs). */
export interface P0TrackInput {
  id: string;
  title?: string;
  artist?: string;
  bpm?: number;
  key?: string;        // Camelot (e.g. "8A") or musical
  energy?: number;     // 0..1
  danceability?: number; // 0..1
  genre?: string;
  mood?: string;
  hasVocals?: boolean;
  year?: number;
  tags?: string[];
}

// ============================================================================
// PROMPT 111 (was 97) — Natural Language Crate / Set Builder
// ============================================================================
// Parses a plain-English description into draft smart-crate rules + a candidate track list +
// suggested set sections, WITHOUT mutating anything. The caller reviews and applies.

/** A track field the natural-language builder can parse a filter rule for. */
export type NlBuilderField = "bpm" | "energy" | "danceability" | "key" | "genre" | "mood" | "year" | "artist";
/** The comparison operator for a parsed natural-language builder rule. */
export type NlBuilderOp = "gte" | "lte" | "eq" | "between" | "contains";

/** A single filter rule extracted from a natural-language crate/set description. */
export interface NlBuilderRule {
  field: NlBuilderField;
  op: NlBuilderOp;
  value: number | string;
  value2?: number; // for "between"
  sourcePhrase: string; // the words that produced this rule (explainability)
}

/** A suggested set section (e.g. warmup/peak/cooldown) inferred from a natural-language description. */
export interface NlBuilderSection {
  label: string;
  intent: string;         // "warmup" | "peak" | "cooldown" | ...
  targetEnergyMin: number;
  targetEnergyMax: number;
}

/** The draft result of parsing a natural-language crate/set request: rules, sections, and candidate tracks. */
export interface NlBuilderDraft {
  id: string;
  query: string;
  rules: NlBuilderRule[];
  sections: NlBuilderSection[];
  candidateTrackIds: string[];
  explanation: string;
  confidence: number;
  warnings: string[];
  createdAt: string;
}

/** Parses natural-language crate/set descriptions into draft rules, sections, and candidate tracks. */
export class DJSEMNlSetBuilderService {
  /**
   * Parses a NL query into draft rules/sections/candidates. Pure — applies nothing.
   * @param query - the free-text crate/set description.
   * @param tracks - the candidate track pool to filter against the parsed rules.
   * @returns a draft with parsed rules, inferred sections, matching candidate track ids, and a confidence score.
   */
  buildDraft(query: string, tracks: P0TrackInput[]): NlBuilderDraft {
    const q = (query ?? "").toLowerCase();
    const rules: NlBuilderRule[] = [];
    const warnings: string[] = [];

    // --- numeric BPM: "128 bpm", "between 120 and 130 bpm", "under 125 bpm" ---
    const bpmBetween = q.match(/between\s+(\d{2,3})\s+and\s+(\d{2,3})\s*bpm/);
    const bpmUnder = q.match(/(?:under|below|<)\s*(\d{2,3})\s*bpm/);
    const bpmOver = q.match(/(?:over|above|>)\s*(\d{2,3})\s*bpm/);
    const bpmExact = q.match(/(\d{2,3})\s*bpm/);
    if (bpmBetween) rules.push({ field: "bpm", op: "between", value: +bpmBetween[1], value2: +bpmBetween[2], sourcePhrase: bpmBetween[0] });
    else if (bpmUnder) rules.push({ field: "bpm", op: "lte", value: +bpmUnder[1], sourcePhrase: bpmUnder[0] });
    else if (bpmOver) rules.push({ field: "bpm", op: "gte", value: +bpmOver[1], sourcePhrase: bpmOver[0] });
    else if (bpmExact) rules.push({ field: "bpm", op: "eq", value: +bpmExact[1], sourcePhrase: bpmExact[0] });

    // --- energy words ---
    if (/\b(high[- ]?energy|peak|banger|hard)\b/.test(q)) rules.push({ field: "energy", op: "gte", value: 0.7, sourcePhrase: "high energy" });
    if (/\b(low[- ]?energy|chill|warmup|warm[- ]?up|mellow|deep)\b/.test(q)) rules.push({ field: "energy", op: "lte", value: 0.45, sourcePhrase: "low energy / chill" });

    // --- genre / mood free-text ---
    const genreMatch = q.match(/\b(techno|house|trance|dnb|drum\s?&?\s?bass|disco|ambient|hip[- ]?hop|afro|melodic)\b/);
    if (genreMatch) rules.push({ field: "genre", op: "contains", value: genreMatch[1].replace(/\s+/g, ""), sourcePhrase: genreMatch[0] });

    // --- year: "from 2023", "since 2020", "new" ---
    const yearMatch = q.match(/(?:from|since|after)\s+(\d{4})/);
    if (yearMatch) rules.push({ field: "year", op: "gte", value: +yearMatch[1], sourcePhrase: yearMatch[0] });
    else if (/\b(new|recent|latest)\b/.test(q)) rules.push({ field: "year", op: "gte", value: new Date().getUTCFullYear() - 2, sourcePhrase: "new/recent" });

    if (rules.length === 0) warnings.push("Could not extract any filters from the description — try mentioning BPM, energy, genre, or year.");

    // --- evaluate candidates against parsed rules (AND) ---
    const candidates = tracks.filter((t) => rules.every((r) => matchRule(t, r)));

    // --- infer set sections from the query ---
    const sections: NlBuilderSection[] = [];
    if (/\b(warmup|warm[- ]?up|opener|open)\b/.test(q)) sections.push({ label: "Warmup", intent: "warmup", targetEnergyMin: 0.2, targetEnergyMax: 0.5 });
    if (/\b(peak|main|drop|banger)\b/.test(q)) sections.push({ label: "Peak", intent: "peak", targetEnergyMin: 0.7, targetEnergyMax: 1 });
    if (/\b(cool[- ]?down|closer|closing|outro|end)\b/.test(q)) sections.push({ label: "Closing", intent: "cooldown", targetEnergyMin: 0.2, targetEnergyMax: 0.55 });
    if (sections.length === 0 && candidates.length > 0) {
      // default three-act shape
      sections.push({ label: "Warmup", intent: "warmup", targetEnergyMin: 0.2, targetEnergyMax: 0.5 });
      sections.push({ label: "Peak", intent: "peak", targetEnergyMin: 0.7, targetEnergyMax: 1 });
      sections.push({ label: "Closing", intent: "cooldown", targetEnergyMin: 0.2, targetEnergyMax: 0.55 });
    }

    const confidence = rules.length === 0 ? 0.1 : Math.min(0.9, 0.4 + rules.length * 0.15);
    const explanation = rules.length
      ? `Parsed ${rules.length} filter(s): ${rules.map((r) => `${r.field} ${r.op} ${r.value}${r.value2 !== undefined ? `–${r.value2}` : ""}`).join(", ")}. Matched ${candidates.length} of ${tracks.length} tracks.`
      : "No filters parsed; showing no candidates.";

    return {
      id: `nlb_${_seq.next()}`, query: query ?? "", rules, sections,
      candidateTrackIds: candidates.map((t) => t.id),
      explanation, confidence, warnings, createdAt: AT,
    };
  }
}

function matchRule(t: P0TrackInput, r: NlBuilderRule): boolean {
  switch (r.field) {
    case "bpm": return numCmp(t.bpm, r);
    case "energy": return numCmp(t.energy, r);
    case "danceability": return numCmp(t.danceability, r);
    case "year": return numCmp(t.year, r);
    case "genre": return strContains(t.genre, r.value);
    case "mood": return strContains(t.mood, r.value);
    case "artist": return strContains(t.artist, r.value);
    case "key": return (t.key ?? "").toLowerCase() === String(r.value).toLowerCase();
    default: return false;
  }
}
function numCmp(v: number | undefined, r: NlBuilderRule): boolean {
  if (v === undefined) return false;
  const a = Number(r.value);
  switch (r.op) {
    case "gte": return v >= a;
    case "lte": return v <= a;
    case "eq": return Math.abs(v - a) <= (r.field === "bpm" ? 2 : 0.05);
    case "between": return v >= a && v <= Number(r.value2 ?? a);
    default: return false;
  }
}
function strContains(v: string | undefined, needle: number | string): boolean {
  if (!v) return false;
  return v.toLowerCase().includes(String(needle).toLowerCase());
}

// ============================================================================
// PROMPT 112 (was 98) — Set Readiness Score
// ============================================================================
// Scores whether a set is ready for a given target (rehearsal / export / client / live).
// Different targets require different gates, so the same set can be "ready to rehearse" but
// "not ready for live". Produces a checklist with pass/fail + blocking severity.

/** The use-case a set's readiness is being scored against; each target gates on different checks. */
export type ReadinessTarget = "rehearsal" | "export" | "client_preview" | "live";

/** A single pass/fail readiness gate evaluated for a set. */
export interface ReadinessCheck {
  id: string;
  label: string;
  passed: boolean;
  blocking: boolean;    // if true and failed, set is NOT ready for the target
  detail: string;
}

/** The full readiness verdict for a set against a specific target, with its underlying checks. */
export interface ReadinessReport {
  id: string;
  setId: string;
  target: ReadinessTarget;
  ready: boolean;
  score: number;        // 0..1 (fraction of checks passed, weighted)
  checks: ReadinessCheck[];
  summary: string;
  createdAt: string;
}

/** Scores whether a set is ready for a given target (rehearsal, export, client preview, or live). */
export class DJSEMSetReadinessService {
  /**
   * Runs the readiness checklist for a set against a target, gating on metadata completeness,
   * minimum track count, and (for 2+ tracks) transition safety.
   * @param setId - id of the set being scored (attached to the report).
   * @param target - which use-case to gate readiness for.
   * @param tracks - the set's tracks in order.
   * @returns a readiness report with pass/fail checks, a weighted score, and an overall ready flag.
   */
  scoreSet(setId: string, target: ReadinessTarget, tracks: P0TrackInput[]): ReadinessReport {
    const checks: ReadinessCheck[] = [];
    const push = (label: string, passed: boolean, blocking: boolean, detail: string) =>
      checks.push({ id: `rc_${_seq.next()}`, label, passed, blocking, detail });

    const hasTracks = tracks.length > 0;
    push("Set has tracks", hasTracks, true, `${tracks.length} track(s)`);

    const missingBpm = tracks.filter((t) => t.bpm === undefined).length;
    const missingKey = tracks.filter((t) => !t.key).length;
    const missingEnergy = tracks.filter((t) => t.energy === undefined).length;

    // Metadata completeness — blocking for export/client/live, advisory for rehearsal.
    const metaBlocking = target !== "rehearsal";
    push("BPM present on all tracks", missingBpm === 0, metaBlocking, `${missingBpm} missing`);
    push("Key present on all tracks", missingKey === 0, metaBlocking, `${missingKey} missing`);
    push("Energy present on all tracks", missingEnergy === 0, false, `${missingEnergy} missing`);

    // Minimum length gates by target.
    const minLen = target === "live" ? 5 : target === "client_preview" ? 3 : 1;
    push(`At least ${minLen} track(s)`, tracks.length >= minLen, target !== "rehearsal", `${tracks.length}/${minLen}`);

    // Transition safety — for live, no critical adjacent clashes allowed.
    if (tracks.length >= 2) {
      const analyzer = new DJSEMTransitionRiskAnalyzer();
      let worst = 1;
      for (let i = 0; i < tracks.length - 1; i++) {
        const r = analyzer.analysePair({
          fromTrack: { id: tracks[i].id, bpm: tracks[i].bpm, key: tracks[i].key, energy: tracks[i].energy, hasVocals: tracks[i].hasVocals },
          toTrack: { id: tracks[i + 1].id, bpm: tracks[i + 1].bpm, key: tracks[i + 1].key, energy: tracks[i + 1].energy, hasVocals: tracks[i + 1].hasVocals },
        });
        worst = Math.min(worst, r.compatibilityScore);
      }
      push("No high-risk transitions", worst >= 0.3, target === "live", `worst compatibility ${(worst * 100).toFixed(0)}%`);
    }

    const weighted = checks.reduce((acc, c) => acc + (c.passed ? (c.blocking ? 2 : 1) : 0), 0);
    const maxWeighted = checks.reduce((acc, c) => acc + (c.blocking ? 2 : 1), 0);
    const score = maxWeighted ? weighted / maxWeighted : 0;
    const ready = checks.every((c) => c.passed || !c.blocking);

    return {
      id: `rr_${_seq.next()}`, setId, target, ready, score, checks,
      summary: ready
        ? `Ready for ${target.replace("_", " ")} (${(score * 100).toFixed(0)}%).`
        : `Not ready for ${target.replace("_", " ")} — ${checks.filter((c) => c.blocking && !c.passed).length} blocking issue(s).`,
      createdAt: AT,
    };
  }
}

// ============================================================================
// PROMPT 113 (was 99) — Transition Risk Heatmap
// ============================================================================
// Builds a per-transition risk row across the whole set so the UI can render a heatmap.
// Reuses the canonical DJSEMTransitionRiskAnalyzer for each adjacent pair rather than
// duplicating transition logic.

/** Qualitative risk band for a single transition, derived from its compatibility score. */
export type RiskBand = "safe" | "caution" | "risk" | "danger";

/** A single transition's risk data for one adjacent track pair within a set. */
export interface TransitionHeatCell {
  index: number;         // transition i → i+1
  fromTrackId: string;
  toTrackId: string;
  compatibility: number; // 0..1
  band: RiskBand;
  topReasons: string[];
}

/** A full per-transition risk heatmap for a set, with aggregate stats across all adjacent pairs. */
export interface TransitionHeatmap {
  id: string;
  setId: string;
  cells: TransitionHeatCell[];
  worstIndex: number | null;
  averageCompatibility: number;
  bandCounts: Record<RiskBand, number>;
  createdAt: string;
}

/** Builds a per-transition risk heatmap across an entire set for UI rendering. */
export class DJSEMTransitionHeatmapService {
  /**
   * Computes a transition risk cell for every adjacent track pair in the set.
   * @param setId - id of the set being analysed (attached to the heatmap).
   * @param tracks - the set's tracks in order.
   * @returns a heatmap with one cell per transition, the worst transition's index, and aggregate stats.
   */
  build(setId: string, tracks: P0TrackInput[]): TransitionHeatmap {
    const analyzer = new DJSEMTransitionRiskAnalyzer();
    const cells: TransitionHeatCell[] = [];
    const bandCounts: Record<RiskBand, number> = { safe: 0, caution: 0, risk: 0, danger: 0 };

    for (let i = 0; i < tracks.length - 1; i++) {
      const a = tracks[i], b = tracks[i + 1];
      const r = analyzer.analysePair({
        fromTrack: { id: a.id, bpm: a.bpm, key: a.key, energy: a.energy, hasVocals: a.hasVocals },
        toTrack: { id: b.id, bpm: b.bpm, key: b.key, energy: b.energy, hasVocals: b.hasVocals },
      });
      const band = bandFor(r.compatibilityScore);
      bandCounts[band]++;
      cells.push({
        index: i, fromTrackId: a.id, toTrackId: b.id,
        compatibility: r.compatibilityScore, band,
        topReasons: r.issues.slice(0, 3).map((iss) => iss.explanation),
      });
    }

    const avg = cells.length ? cells.reduce((s, c) => s + c.compatibility, 0) / cells.length : 1;
    let worstIndex: number | null = null;
    let worst = Infinity;
    for (const c of cells) if (c.compatibility < worst) { worst = c.compatibility; worstIndex = c.index; }

    return { id: `thm_${_seq.next()}`, setId, cells, worstIndex, averageCompatibility: avg, bandCounts, createdAt: AT };
  }
}

function bandFor(score: number): RiskBand {
  if (score >= 0.7) return "safe";
  if (score >= 0.5) return "caution";
  if (score >= 0.3) return "risk";
  return "danger";
}
